import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import {
  FaBell,
  FaBoxes,
  FaChartLine,
  FaClipboardList,
  FaCreditCard,
  FaGasPump,
  FaHome,
  FaLayerGroup,
  FaLock,
  FaMoneyBillWave,
  FaMoon,
  FaOilCan,
  FaPercentage,
  FaRobot,
  FaSignOutAlt,
  FaTags,
  FaTimes,
  FaTruck,
  FaUserShield,
  FaUsers,
  FaWallet,
  FaWhatsapp,
} from 'react-icons/fa';
import api from '../lib/api';

import Dashboard from '../pages/Dashboard';
import Users from '../pages/Users';
import Nozzles from '../pages/Nozzles';
import Employees from '../pages/Employees';
import Stock from '../pages/Stock';
import Readings from '../pages/Readings';
import EmployeeCredit from '../pages/EmployeeCredit';
import Customercredit from '../pages/Customercredit';
import NozzleReport from '../pages/NozzleReport';
import AdminLubeStock from '../pages/AdminLubeStock';
import Expensemgmt from '../pages/Expenses';
import Vendors from '../pages/VendorMaster';
import ExpenseCategoryMaster from '../pages/ExpenseCategoryMaster';
import MarginMaster from '../pages/MarginMaster';
import Whatsappsummary from '../pages/WhatsappSummarySettings';
import DiscountManagement from '../pages/DiscountManagement';
import CashTracing from '../pages/CashTracing';
import PampaBot from '../pages/PampaBot';
import PampaChatWidget from '../pages/PampaChatWidget';
import './AdminLayout.css';

import BPCLLogo from '../pages/CSS/BPCL.png';
import FuelFlowLogo from '../pages/CSS/fuelFlowLogo.png';
import HPCLLogo from '../pages/CSS/HPCL.png';
import IOCLLogo from '../pages/CSS/IOCL.png';
import JIOBPLogo from '../pages/CSS/JIOBP.png';
import MRPLLogo from '../pages/CSS/MRPL.png';
import NayaraLogo from '../pages/CSS/NAYARA.png';
import ShellLogo from '../pages/CSS/Shell.png';

const companyLogoMap = {
  BPCL: BPCLLogo,
  HPCL: HPCLLogo,
  IOCL: IOCLLogo,
  'JIO BP': JIOBPLogo,
  MRPL: MRPLLogo,
  NAYARA: NayaraLogo,
  SHELL: ShellLogo,
};

const permissions = {
  admin: [
    'dashboard',
    'users',
    'employees',
    'nozzles',
    'stock',
    'readings',
    'employee-credit',
    'customer-credit',
    'nozzle-report',
    'lube-stock',
    'expenses',
    'vendors',
    'expense-categories',
    'margins',
    'whatsapp-summary',
    'discounts',
    'cash-tracing',
    'pampa',
  ],
  manager: [
    'dashboard',
    'employees',
    'nozzles',
    'readings',
    'employee-credit',
    'customer-credit',
    'nozzle-report',
    'lube-stock',
    'expenses',
    'vendors',
    'discounts',
    'cash-tracing',
    'pampa',
  ],
};

const navSections = [
  {
    title: 'Command',
    items: [
      { key: 'dashboard', label: 'Dashboard', to: '/admin/dashboard', icon: FaHome },
      { key: 'readings', label: 'Readings', to: '/admin/readings', icon: FaChartLine },
      { key: 'cash-tracing', label: 'Cash Tracing', to: '/admin/cash-tracing', icon: FaWallet },
    ],
  },
  {
    title: 'Station Setup',
    items: [
      { key: 'users', label: 'Users', to: '/admin/users', icon: FaUserShield },
      { key: 'employees', label: 'Employees', to: '/admin/employees', icon: FaUsers },
      { key: 'nozzles', label: 'Pump & Nozzles', to: '/admin/nozzles', icon: FaGasPump },
      { key: 'stock', label: 'Fuel Stock', to: '/admin/stock', icon: FaBoxes },
    ],
  },
  {
    title: 'Finance',
    items: [
      { key: 'employee-credit', label: 'Employee Credit', to: '/admin/employee-credit', icon: FaCreditCard },
      { key: 'customer-credit', label: 'Customer Credit', to: '/admin/customer-credit', icon: FaMoneyBillWave },
      { key: 'expenses', label: 'Expenses', to: '/admin/expenses', icon: FaClipboardList },
      { key: 'expense-categories', label: 'Expense Categories', to: '/admin/expense-categories', icon: FaLayerGroup },
      { key: 'margins', label: 'Margins', to: '/admin/margins', icon: FaPercentage },
      { key: 'discounts', label: 'Discounts', to: '/admin/discounts', icon: FaTags },
    ],
  },
  {
    title: 'Inventory & Reports',
    items: [
      { key: 'nozzle-report', label: 'Reports', to: '/admin/nozzle-report', icon: FaClipboardList },
      { key: 'lube-stock', label: 'Lube Stock', to: '/admin/lube-stock', icon: FaOilCan },
      { key: 'vendors', label: 'Vendor Master', to: '/admin/vendors', icon: FaTruck },
      { key: 'whatsapp-summary', label: 'WhatsApp Settings', to: '/admin/whatsapp-summary', icon: FaWhatsapp },
    ],
  },
];

function readLS(key, fallback = '') {
  const raw = localStorage.getItem(key);
  if (raw === null || raw === undefined) return fallback;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return raw;
  }
}

function cleanStoredText(value, fallback) {
  return String(value || fallback)
    .replace(/^["']+|["']+$/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [currentISTTime, setCurrentISTTime] = useState(new Date());
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const role = String(readLS('role', '')).toLowerCase();
  const company = cleanStoredText(readLS('company', ''), '').toUpperCase();
  const companyLogo = companyLogoMap[company] || FuelFlowLogo;
  const roleLabel = role === 'manager' ? 'Manager' : 'Admin';

  const allowed = useMemo(() => new Set(permissions[role] || []), [role]);
  const can = (key) => allowed.has(key);

  const fullUserName = useMemo(() => cleanStoredText(readLS('name', 'User'), 'User'), []);
  const fullPumpName = useMemo(
    () => cleanStoredText(readLS('bunkname', 'Fuel Station'), 'Fuel Station'),
    []
  );

  const visibleSections = useMemo(
    () =>
      navSections
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => allowed.has(item.key)),
        }))
        .filter((section) => section.items.length > 0),
    [allowed]
  );

  const visibleItems = useMemo(
    () => visibleSections.flatMap((section) => section.items),
    [visibleSections]
  );

  const currentModule = useMemo(() => {
    return (
      visibleItems.find((item) => location.pathname === item.to) ||
      visibleItems.find((item) => location.pathname.startsWith(item.to)) ||
      visibleItems[0]
    );
  }, [location.pathname, visibleItems]);

  const istDate = useMemo(() => {
    return currentISTTime.toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }, [currentISTTime]);

  const istTime = useMemo(() => {
    return currentISTTime.toLocaleTimeString('en-IN', {
      timeZone: 'Asia/Kolkata',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  }, [currentISTTime]);

  useEffect(() => {
    if (!role || !permissions[role]) {
      navigate('/');
    }
  }, [role, navigate]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 992px)');
    const setByViewport = () => setSidebarOpen(mq.matches);
    setByViewport();
    mq.addEventListener('change', setByViewport);
    return () => mq.removeEventListener('change', setByViewport);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentISTTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const closeSidebarOnMobile = () => {
    if (window.innerWidth < 992) {
      setSidebarOpen(false);
    }
  };

  const logout = () => {
    localStorage.clear();
    navigate('/');
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      alert('Please fill in all password fields.');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert('New password and confirm password do not match.');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      alert('New password must be at least 6 characters long.');
      return;
    }

    try {
      setSavingPassword(true);
      const token = localStorage.getItem('token');
      await api.post(
        '/api/auth/change-password',
        {
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      alert('Password updated successfully.');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordModal(false);
    } catch (err) {
      const message = err?.response?.data?.error || 'Unable to update password.';
      alert(message);
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className={`admin-shell ${sidebarOpen ? 'with-sb' : 'no-sb'}`}>
      <button
        className={`sidebar-overlay ${sidebarOpen ? 'show' : ''}`}
        onClick={() => setSidebarOpen(false)}
        aria-label="Close sidebar"
        type="button"
      />

      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="admin-sidebar-brand">
          <div className="admin-brand-lockup">
            <img src={companyLogo} alt={company || 'FuelFlow'} className="admin-brand-logo" />
            <div>
              <div className="admin-brand-name">{fullPumpName || 'Fuel Station'}</div>
              <div className="admin-brand-meta">{company || 'FuelFlow'} Command Center</div>
            </div>
          </div>

          <button
            className="admin-icon-btn admin-close-btn"
            aria-label="Close sidebar"
            onClick={() => setSidebarOpen(false)}
            type="button"
          >
            <FaTimes aria-hidden="true" />
          </button>
        </div>

        <nav className="admin-menu" aria-label="Admin navigation">
          {visibleSections.map((section) => (
            <div className="admin-menu-section" key={section.title}>
              <div className="admin-menu-heading">{section.title}</div>
              {section.items.map(({ icon: Icon, label, to }) => (
                <NavLink
                  to={to}
                  className={({ isActive }) =>
                    `admin-menu-link ${isActive ? 'active' : ''}`
                  }
                  onClick={closeSidebarOnMobile}
                  key={to}
                >
                  <span className="admin-menu-icon">
                    <Icon aria-hidden="true" />
                  </span>
                  <span className="admin-menu-text">{label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="admin-sidebar-footer">
          <div className="admin-user-mini">
            <div className="admin-avatar">{fullUserName.charAt(0).toUpperCase() || 'U'}</div>
            <div>
              <strong>{fullUserName}</strong>
              <span>{roleLabel}</span>
            </div>
          </div>

          <button className="admin-logout-btn" onClick={logout} type="button">
            <FaSignOutAlt aria-hidden="true" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      <main className="admin-main">
        <header className="admin-floating-topbar">
          <button
            className="admin-topbar-menu"
            aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
            onClick={() => setSidebarOpen((open) => !open)}
            type="button"
          >
            <span />
            <span />
            <span />
          </button>

          <div className="admin-topbar-title">
            <div className="admin-topbar-kicker">Admin Workspace</div>
            <h1>{currentModule?.label || 'Dashboard'}</h1>
            <p>{fullPumpName}</p>
          </div>

          <div className="admin-topbar-identity">
            <img src={companyLogo} alt={company || 'FuelFlow'} className="admin-topbar-logo" />
            <div className="admin-topbar-userblock">
              <strong title={fullUserName}>{fullUserName}</strong>
              <span>{roleLabel}</span>
            </div>
          </div>

          <div className="admin-topbar-actions">
            <button className="admin-quick-btn" type="button" aria-label="Notifications">
              <FaBell aria-hidden="true" />
            </button>
            <button
              className="admin-quick-btn"
              type="button"
              aria-label="Change password"
              onClick={() => setShowPasswordModal(true)}
            >
              <FaLock aria-hidden="true" />
            </button>
            <button className="admin-quick-btn" type="button" aria-label="Theme">
              <FaMoon aria-hidden="true" />
            </button>
            <div className="admin-ist-clock">
              <span>IST</span>
              <strong>{istTime}</strong>
              <em>{istDate}</em>
            </div>
          </div>
        </header>

        <div className="admin-content">
          <Routes>
            {can('dashboard') && <Route path="dashboard" element={<Dashboard />} />}
            {can('users') && <Route path="users" element={<Users />} />}
            {can('expense-categories') && (
              <Route path="expense-categories" element={<ExpenseCategoryMaster />} />
            )}
            {can('margins') && <Route path="margins" element={<MarginMaster />} />}
            {can('employees') && <Route path="employees" element={<Employees />} />}
            {can('nozzles') && <Route path="nozzles" element={<Nozzles />} />}
            {can('readings') && <Route path="readings" element={<Readings />} />}
            {can('pampa') && <Route path="pampa" element={<PampaBot />} />}
            {can('employee-credit') && (
              <Route path="employee-credit" element={<EmployeeCredit />} />
            )}
            {can('customer-credit') && (
              <Route path="customer-credit" element={<Customercredit />} />
            )}
            {can('lube-stock') && <Route path="lube-stock" element={<AdminLubeStock />} />}
            {can('expenses') && <Route path="expenses" element={<Expensemgmt />} />}
            {can('vendors') && <Route path="vendors" element={<Vendors />} />}
            {can('discounts') && <Route path="discounts" element={<DiscountManagement />} />}
            {can('cash-tracing') && <Route path="cash-tracing" element={<CashTracing />} />}
            {can('nozzle-report') && <Route path="nozzle-report" element={<NozzleReport />} />}
            {can('stock') && <Route path="stock" element={<Stock />} />}
            {can('whatsapp-summary') && (
              <Route path="whatsapp-summary" element={<Whatsappsummary />} />
            )}
            <Route path="*" element={<Dashboard />} />
          </Routes>
        </div>
      </main>

      <PampaChatWidget />

      {showPasswordModal && (
        <div className="admin-modal-backdrop" role="presentation" onClick={() => setShowPasswordModal(false)}>
          <div
            className="admin-modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="change-password-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="admin-modal-header">
              <div>
                <h2 id="change-password-title">Change Password</h2>
                <p>Update the password for your current admin or manager login.</p>
              </div>
              <button
                type="button"
                className="admin-modal-close"
                aria-label="Close change password dialog"
                onClick={() => setShowPasswordModal(false)}
              >
                <FaTimes aria-hidden="true" />
              </button>
            </div>

            <form className="admin-modal-form" onSubmit={handlePasswordChange}>
              <label className="admin-modal-field">
                <span>Current password</span>
                <input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) =>
                    setPasswordForm((prev) => ({ ...prev, currentPassword: e.target.value }))
                  }
                  placeholder="Enter current password"
                  autoComplete="current-password"
                />
              </label>

              <label className="admin-modal-field">
                <span>New password</span>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) =>
                    setPasswordForm((prev) => ({ ...prev, newPassword: e.target.value }))
                  }
                  placeholder="Enter new password"
                  autoComplete="new-password"
                />
              </label>

              <label className="admin-modal-field">
                <span>Confirm new password</span>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) =>
                    setPasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }))
                  }
                  placeholder="Re-enter new password"
                  autoComplete="new-password"
                />
              </label>

              <div className="admin-modal-actions">
                <button
                  type="button"
                  className="admin-modal-secondary"
                  onClick={() => setShowPasswordModal(false)}
                  disabled={savingPassword}
                >
                  Cancel
                </button>
                <button type="submit" className="admin-modal-primary" disabled={savingPassword}>
                  {savingPassword ? 'Saving...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
